import { addMinutes, format, isValid, parseISO } from 'date-fns'

// Seen 3 different formats for time, so just checking for the date portion.
const serverTimeRegExp = RegExp('^[0-9]{4}-[0-9]{2}-[0-9]{2}.*$')

export const isDateFormat = (value: string) => {
  return serverTimeRegExp.test(value)
}
export const convertToUtcDate = (date: Date) => {
  const offsetMinutes = date.getTimezoneOffset()
  const utcTime = addMinutes(date, offsetMinutes)

  return utcTime
}

export const convertUtctoLocalDate = (date: Date) => {
  const offsetMinutes = date.getTimezoneOffset()
  const utcTime = addMinutes(date, -1 * offsetMinutes)

  return utcTime
}

export const parseUTCDateString = (isoString: string): Date | undefined => {
  if (!isoString) return undefined

  try {
    const parsed = parseISO(isoString)
    if (!isValid(parsed)) return undefined

    return new Date(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth(),
      parsed.getUTCDate(),
      parsed.getUTCHours(),
      parsed.getUTCMinutes(),
      parsed.getUTCSeconds(),
      parsed.getUTCMilliseconds(),
    )
  } catch (e) {
    return undefined
  }
}

/**
 * Use with dates parsed via parseUTCDateString for consistent UTC display.
 */
export const formatUTCDate = (
  date: Date | undefined,
  formatStr: string,
): string => {
  if (!date || !isValid(date)) return ''

  return format(date, formatStr)
}

/**
 * filter date time format
 * some date: 2023-11-19Z
 */
/**
 * Parses date / datetime string into local Date object.
 *
 * Does not convert UTC to local time, as all times are stored in UTC.
 * Times must simply pass through to the UI.
 */
export const parseDate = (value: string) => {
  try {
    if (!isValid(parseISO(value))) return undefined
    if (value.includes(':')) {
      // z replacement fixes firefox bug
      return parseISO(value.replace('Z', ''))
    }

    return parseISO(value.replace('Z', ''))
  } catch (e) {
    return undefined
  }
}

export type FormatDateOptions = {
  dateIncludeTime?: boolean
}

export const formatDate = (
  value: string,
  { dateIncludeTime = false }: FormatDateOptions = {},
) => {
  const date = parseDate(value)

  if (!date) return date

  if (format(date, 'HH:mm:ss') === '00:00:00' || !dateIncludeTime) {
    return format(date, 'MMM d, yyyy')
  }

  return format(date, 'MMM d, yyyy HH:mm:ss')
}

export const formatAdminDateTime = (value: string) => {
  const date = parseDate(value)

  if (!date) return undefined

  return format(date, 'MMM d, yy HH:mm:ss')
}

/**
 * Short admin date: `MMM d, yy` (e.g. "Apr 23, 26"). Pairs with
 * {@link formatAdminTime} for places that want the date and time stacked.
 */
export const formatAdminDate = (value: string) => {
  const date = parseDate(value)

  if (!date) return undefined

  return format(date, 'MMM d, yy')
}

/**
 * Admin time: `HH:mm:ss`. Pairs with {@link formatAdminDate} for stacked
 * date/time displays (e.g. the Customer Data API config version rail).
 */
export const formatAdminTime = (value: string) => {
  const date = parseDate(value)

  if (!date) return undefined

  return format(date, 'HH:mm:ss')
}

/**
 * Convert date to string in ISO-8601 DateTime format (yyyy-MM-ddTHH:mm:ss.000Z) used by the server.
 * Uses local date components to create a UTC date, preserving the selected date regardless of timezone.
 */
export const dateToDateString = (date: Date) => {
  // Use local date components to preserve the selected date, then create UTC date
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  const hours = date.getHours()
  const minutes = date.getMinutes()
  const seconds = date.getSeconds()
  const milliseconds = date.getMilliseconds()

  // Create a UTC date using the local date components to preserve the selected date
  const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0))

  return utcDate.toISOString()
}
